<div class="flex flex-col flex-1 px-3 py-2 max-w-6xl mx-auto w-full">
  <div class="flex items-center mb-1">
    <h1 class="text-base font-semibold">Reports</h1>
    <div class="ml-auto text-xs text-gray-400">
      {{ fromIso | slice: 0 : 16 }} → {{ toIso | slice: 0 : 16 }} UTC
    </div>
  </div>
  <p class="text-sm text-gray-500 mb-3">
    Aggregate volumes across sessions, requests, NDC methods and outcomes. Served from
    pre-aggregated rollups.
  </p>

  @if (error) {
    <div class="alert alert-error text-sm mb-3">{{ error }}</div>
  }

  <!-- ===== Period picker ===== -->
  <div class="card border border-gray-200 rounded-lg mb-4">
    <div class="px-4 py-3 flex flex-wrap items-center gap-x-5 gap-y-2">
      @for (group of periodGroups; track group) {
        <div class="flex items-center gap-1.5">
          <span class="text-[11px] uppercase tracking-wide text-gray-400 mr-1">{{ group }}</span>
          @for (p of periodsInGroup(group); track p.key) {
            <button
              class="btn btn-xs"
              [ngClass]="
                selectedPeriodKey === p.key ? 'btn-primary' : 'btn-ghost border border-gray-200'
              "
              (click)="selectPeriod(p.key)"
            >
              {{ p.label }}
            </button>
          }
        </div>
      }
    </div>
  </div>

  <!-- ===== KPI cards ===== -->
  <div class="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
    <div class="card border border-gray-200 rounded-lg px-4 py-3">
      <div class="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
        <span class="inline-block w-2 h-2 rounded-full" style="background: #2563eb"></span>
        Sessions
      </div>
      <div class="text-2xl font-bold mt-1">
        {{ totals ? fmtCompact(totals.sessions) : '—' }}
      </div>
      <div class="text-xs text-gray-400 mt-0.5">{{ totals ? (totals.sessions | number) : '' }}</div>
    </div>
    <div class="card border border-gray-200 rounded-lg px-4 py-3">
      <div class="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
        <span class="inline-block w-2 h-2 rounded-full" style="background: #cd0005"></span>
        Requests
      </div>
      <div class="text-2xl font-bold mt-1">
        {{ totals ? fmtCompact(totals.requests) : '—' }}
      </div>
      <div class="text-xs text-gray-400 mt-0.5">{{ totals ? (totals.requests | number) : '' }}</div>
    </div>
    <div class="card border border-gray-200 rounded-lg px-4 py-3">
      <div class="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
        <span class="inline-block w-2 h-2 rounded-full" style="background: #7c3aed"></span>
        Messages
        <span
          class="text-gray-300 normal-case"
          title="Estimated (requests × ~4); messages are not rolled up."
        >
          ≈
        </span>
      </div>
      <div class="text-2xl font-bold mt-1">
        {{ totals ? fmtCompact(totals.messages_est) : '—' }}
      </div>
      <div class="text-xs text-gray-400 mt-0.5">estimated</div>
    </div>
    <div class="card border border-gray-200 rounded-lg px-4 py-3">
      <div class="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
        <span class="inline-block w-2 h-2 rounded-full" style="background: #f59e0b"></span>
        Error rate
      </div>
      <div class="text-2xl font-bold mt-1">
        {{ totals ? (errorRatePct | number: '1.1-1') + '%' : '—' }}
      </div>
      <div class="text-xs text-gray-400 mt-0.5">
        {{ totals ? (totals.errors | number) + ' errors' : '' }}
      </div>
    </div>
  </div>

  <!-- ===== Time series ===== -->
  <div class="card border border-gray-200 rounded-lg mb-4">
    <div class="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
      <h2 class="text-sm font-semibold">Volume over time</h2>
      <span class="ml-auto text-xs text-gray-400">{{ grainLabel() }}</span>
    </div>
    <div class="px-4 py-4">
      <div class="flex items-center gap-2 mb-3">
        <!-- Primary metric toggle -->
        @for (m of ['requests', 'sessions']; track m) {
          <button
            class="btn btn-xs border border-gray-200 capitalize"
            [ngClass]="metric === m ? 'bg-gray-100 font-semibold' : 'btn-ghost'"
            (click)="setMetric($any(m))"
          >
            {{ m }}
          </button>
        }
        <!-- Legend: stacked bands — total metric + error share -->
        <div class="ml-auto flex items-center gap-4 text-xs text-gray-500">
          <span class="flex items-center gap-1.5">
            <span class="inline-block w-3 h-2 rounded-sm" [style.background]="metricColor"></span>
            {{ metricLabel }}
          </span>
          <span class="flex items-center gap-1.5">
            <span class="inline-block w-3 h-2 rounded-sm" [style.background]="errorColor"></span>
            Errors
          </span>
        </div>
      </div>

      @if (loadingSummary) {
        <div class="h-[240px] flex items-center justify-center text-gray-400 text-sm">
          <span class="loading loading-spinner loading-sm mr-2"></span>
          Loading…
        </div>
      } @else if (chart) {
        <svg [attr.viewBox]="viewBox" class="w-full" style="height: 240px">
          @for (g of chart.grid; track g.y) {
            <line
              [attr.x1]="plotLeft"
              [attr.y1]="g.y"
              [attr.x2]="plotRight"
              [attr.y2]="g.y"
              stroke="#eef2f7"
            />
            <text
              [attr.x]="plotLeft - 8"
              [attr.y]="g.y + 4"
              text-anchor="end"
              font-size="10"
              fill="#9ca3af"
            >
              {{ g.label }}
            </text>
          }
          <!-- Stacked bands: rest (top) + errors (bottom) sum to total -->
          <path [attr.d]="chart.restAreaPath" [attr.fill]="chart.color" fill-opacity="0.16" />
          <path [attr.d]="chart.errorAreaPath" [attr.fill]="chart.errorColor" fill-opacity="0.55" />
          <path
            [attr.d]="chart.totalLinePath"
            fill="none"
            [attr.stroke]="chart.color"
            stroke-width="2"
          />
          <path
            [attr.d]="chart.errorLinePath"
            fill="none"
            [attr.stroke]="chart.errorColor"
            stroke-width="1.5"
          />
          @for (p of chart.points; track p.x) {
            <circle [attr.cx]="p.x" [attr.cy]="p.y" r="2.5" [attr.fill]="chart.color">
              <title>{{ p.label }} · {{ p.value | number }} {{ metric }}</title>
            </circle>
          }
          @for (p of chart.errorPoints; track p.x) {
            <circle [attr.cx]="p.x" [attr.cy]="p.y" r="2" [attr.fill]="chart.errorColor">
              <title>
                {{ p.label }} · {{ p.value | number }} errors ({{ p.rate | number: '1.1-1' }}%)
              </title>
            </circle>
          }
          @for (xl of chart.xlabels; track xl.x) {
            <text
              [attr.x]="xl.x"
              [attr.y]="gridBottom + 16"
              text-anchor="middle"
              font-size="10"
              fill="#9ca3af"
            >
              {{ xl.label }}
            </text>
          }
        </svg>
      } @else {
        <div class="h-[240px] flex items-center justify-center text-gray-400 text-sm">
          No data for this period yet.
        </div>
      }
      <div class="text-[11px] text-gray-400 mt-2">
        Buckets: hourly (≤30d) / monthly (>30d) rollups · macro tier retains months of history after
        raw traces are purged at 30 days.
      </div>
    </div>
  </div>

  <!-- ===== Breakdown + outcomes ===== -->
  <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
    <!-- Breakdown -->
    <div class="card border border-gray-200 rounded-lg lg:col-span-2">
      <div class="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
        <h2 class="text-sm font-semibold">Breakdown</h2>
        <span class="ml-auto text-xs text-gray-400">top 8 · selected period</span>
      </div>
      <div class="px-4 py-3">
        <div class="flex gap-1 flex-wrap mb-3">
          @for (d of dimensions; track d.key) {
            <button
              class="btn btn-xs"
              [ngClass]="dimension === d.key ? 'bg-gray-100 font-semibold' : 'btn-ghost'"
              (click)="setDimension(d.key)"
            >
              {{ d.label }}
            </button>
          }
        </div>

        @if (loadingBreakdown) {
          <div class="py-8 text-center text-gray-400 text-sm">
            <span class="loading loading-spinner loading-sm"></span>
          </div>
        } @else if (breakdown && breakdown.rows.length) {
          <table class="table table-sm">
            <thead>
              <tr>
                <th>{{ dimension }}</th>
                <th class="text-right">Requests</th>
                <th>Share</th>
                <th class="text-right">Err %</th>
              </tr>
            </thead>
            <tbody>
              @for (row of breakdown.rows; track row.key) {
                <tr>
                  <td class="font-medium">{{ row.key }}</td>
                  <td class="text-right tabular-nums">{{ row.requests | number }}</td>
                  <td>
                    <div class="flex items-center gap-2">
                      <div class="w-24 h-1.5 rounded bg-gray-100 overflow-hidden">
                        <div
                          class="h-full rounded bg-blue-600"
                          [style.width.%]="(row.requests / breakdown.rows[0].requests) * 100"
                        ></div>
                      </div>
                      <span class="text-xs text-gray-400">
                        {{
                          (row.requests / (totals?.requests || row.requests)) * 100
                            | number: '1.1-1'
                        }}%
                      </span>
                    </div>
                  </td>
                  <td
                    class="text-right tabular-nums"
                    [ngClass]="
                      row.error_rate > 0.05 ? 'text-red-600 font-semibold' : 'text-green-700'
                    "
                  >
                    {{ row.error_rate * 100 | number: '1.1-1' }}%
                  </td>
                </tr>
              }
            </tbody>
          </table>
        } @else {
          <div class="py-8 text-center text-gray-400 text-sm">No data.</div>
        }
      </div>
    </div>

    <!-- Outcomes -->
    <div class="card border border-gray-200 rounded-lg">
      <div class="px-4 py-3 border-b border-gray-200">
        <h2 class="text-sm font-semibold">Outcomes</h2>
      </div>
      <div class="px-4 py-4">
        @if (summary && statusTotal > 0) {
          <div class="flex items-center gap-4">
            <svg width="120" height="120" viewBox="0 0 120 120" class="flex-none">
              @for (seg of donutSegments(); track seg.color) {
                <circle
                  cx="60"
                  cy="60"
                  [attr.r]="donutR"
                  fill="none"
                  [attr.stroke]="seg.color"
                  stroke-width="18"
                  [attr.stroke-dasharray]="seg.dash"
                  [attr.stroke-dashoffset]="seg.offset"
                  transform="rotate(-90 60 60)"
                />
              }
              <text
                x="60"
                y="56"
                text-anchor="middle"
                font-size="17"
                font-weight="700"
                fill="#1f2937"
              >
                {{ okPct | number: '1.0-1' }}%
              </text>
              <text x="60" y="72" text-anchor="middle" font-size="10" fill="#9ca3af">OK</text>
            </svg>
            <div class="flex-1">
              @for (s of summary.status; track s.status) {
                <div class="flex items-center gap-2 text-sm my-1">
                  <span
                    class="w-2.5 h-2.5 rounded-sm"
                    [style.background]="statusColor(s.status)"
                  ></span>
                  <span class="flex-1">{{ statusLabel(s.status) }}</span>
                  <span class="text-gray-400 tabular-nums">
                    {{ statusShare(s.count) * 100 | number: '1.1-1' }}%
                  </span>
                  <span class="w-14 text-right tabular-nums">{{ fmtCompact(s.count) }}</span>
                </div>
              }
            </div>
          </div>
        } @else {
          <div class="py-8 text-center text-gray-400 text-sm">No data.</div>
        }
        <p class="text-[11px] text-gray-400 mt-3 border-l-2 border-gray-200 pl-2">
          Outcome =
          <code class="text-[11px]">requests.aggregate_status</code>
          (ok / warning / error / not_set) rolled up per bucket.
        </p>
      </div>
    </div>
  </div>
</div>
